import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import FiltersSection from '../molecules/FiltersSection/FiltersSection';
import css from './ReportsFilters.css';
import { defaultFilters, reportTypes } from '../../utils/reports';
import { NOT_IN_OPERATOR } from '../../utils/reportsConsts';

const getFiltersByCategory = fields => {
    let category;

    return fields.reduce((acc, field) => {
        if (field.divider) {
            acc[field.display_name] = [];
            category = field.display_name;
            return acc;
        }
        acc[category].push(field.display_name);
        return acc;
    }, {});
};

const ReportsFilters = ({
    onFiltersChange,
    onFilterAdded,
    onFilterRemoved,
    onFiltersCleared,
    filtersFields = [],
    fields = [],
    reportType,
    isLoadedFromUrl = false,
    isEmptyStateMode = false,
}) => {
    const isInitiatedFilters = useRef(false);
    const [filters, setFilters] = useState({});
    const filtersByCategory = useMemo(() => getFiltersByCategory(fields), [fields]);

    const handleFilterTagsChange = (displayName, selectedTags) => {
        onFiltersChange(
            filters[displayName].name,
            selectedTags.map(tag => tag.label)
        );
    };

    const handleFilterTextChange = (displayName, text) => {
        onFiltersChange(filters[displayName].name, text.trim() ? [text] : []);
    };

    const handleFilterAdded = displayName => {
        onFilterAdded(fields.find(field => field.display_name === displayName));
    };

    const handleFilterRemoved = displayName => {
        onFilterRemoved(filtersFields.findIndex(filter => filter.field.display_name === displayName));
    };

    const mandatoryFiltersSet = useMemo(
        () => new Set(defaultFilters(reportType).map(({ displayName }) => displayName)),
        [reportType]
    );

    useEffect(() => {
        if (!filtersFields) {
            const formattedFilters = [...mandatoryFiltersSet].reduce((filtersObj, filter) => {
                filtersObj[filter] = {
                    options: [],
                    loading: true,
                    removable: false,
                };

                return filtersObj;
            }, {});

            setFilters(formattedFilters);
            return;
        }

        const formattedFilters = [...filtersFields].reduce((filtersObj, filter) => {
            const filterName = filter.field.display_name;

            if (filter.isPostProcess) {
                filtersObj[filterName] = {
                    name: filter.field.name,
                    isPostProcess: true,
                    value: filter.tags?.[0]?.name || '',
                    operator: filter.operator?.display_name || '',
                    removable: !mandatoryFiltersSet.has(filterName),
                };
                return filtersObj;
            }

            const availableOptions =
                filter.field.values?.map(option => ({
                    label: option.display_name,
                    value: option.name,
                })) || [];

            let selectedOptions = filter.tags.map(tag => ({
                label: tag.display_name,
                value: tag.name,
            }));

            // If no options are selected and filters are loaded from URL, select all available options.
            if (!selectedOptions.length && isLoadedFromUrl && !isInitiatedFilters.current) {
                onFiltersChange(
                    filter.field.name,
                    availableOptions.map(tag => tag.label)
                );
                selectedOptions = availableOptions;
            }

            // If the filter operator is NOT IN, exclude the selected options from all available options.
            if (filter.operator.name === NOT_IN_OPERATOR) {
                const excludedNames = new Set(selectedOptions.map(opt => opt.value));
                selectedOptions = availableOptions.filter(opt => !excludedNames.has(opt.value));
            }

            filtersObj[filterName] = {
                name: filter.field.name,
                options: availableOptions,
                selectedOptions,
                loading: false,
                removable: !mandatoryFiltersSet.has(filterName),
            };

            return filtersObj;
        }, {});

        isInitiatedFilters.current = true;
        setFilters(formattedFilters);
    }, [filtersFields, mandatoryFiltersSet, isLoadedFromUrl]);

    return (
        <FiltersSection
            filters={filters}
            filtersByCategory={filtersByCategory}
            onFilterAdded={handleFilterAdded}
            onFilterRemoved={handleFilterRemoved}
            onFilterTagsChange={handleFilterTagsChange}
            onFilterTextChange={handleFilterTextChange}
            onClearAll={onFiltersCleared}
            containerClass={css.filtersSection}
            headerTitle="STATIC.PAGES.CREATIVE_EXPLORE.FILTERS_SECTION_TITLE"
            headerIcon="filter-plus"
            openFiltersByDefault
            shouldWarnEmpty={reportType === reportTypes.creativeExplore && !isEmptyStateMode}
        />
    );
};

ReportsFilters.propTypes = {
    onFiltersChange: PropTypes.func.isRequired,
    filtersFields: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.shape({
                name: PropTypes.string,
                display_name: PropTypes.string,
                values: PropTypes.arrayOf(
                    PropTypes.shape({
                        name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                        display_name: PropTypes.string,
                    })
                ),
            }).isRequired,
            tags: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    display_name: PropTypes.string,
                })
            ).isRequired,
        })
    ),
    fields: PropTypes.arrayOf(PropTypes.object),
    onFilterAdded: PropTypes.func,
    onFilterRemoved: PropTypes.func,
    onFiltersCleared: PropTypes.func,
    reportType: PropTypes.string,
    isLoadedFromUrl: PropTypes.bool,
    isEmptyStateMode: PropTypes.bool,
};

export default ReportsFilters;
